import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
 Plus,
 Edit,
 Trash2,
 CheckCircle,
 XCircle,
 AlertCircle,
 Loader2,
 ListChecks,
 Receipt,
 Crown,
 Power,
 Search,
 Link2,
 Unlink,
} from 'lucide-react';
import { Button } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import CreateSubscriptionModal from '../../../components/admin/CreateSubscriptionModal';
import { useToastContext } from '../../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';
import {
 useSubscriptions,
 useSubscriptionStats,
 useCreateSubscription,
 useUpdateSubscription,
 useDeleteSubscription,
 useToggleSubscriptionStatus,
} from '../../../services/subscriptionService';

const STATUS_TABS = [
 { value: 'all', label: 'All', countKey: 'total' },
 { value: 'active', label: 'Active', countKey: 'active' },
 { value: 'inactive', label: 'Inactive', countKey: 'inactive' },
];

const money = (amount, currency = 'USD') => {
 const value = Number(amount);
 const code = String(currency || 'USD').toUpperCase();
 if (!Number.isFinite(value)) return `${code} 0.00`;
 try {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: code,
 currencyDisplay: 'symbol',
 }).format(value);
 } catch {
 return `${code} ${value.toFixed(2)}`;
 }
};

const MetricCard = ({ label, value, hint, icon: Icon, tone = 'sky' }) => {
 const tones = {
 sky: 'bg-surface-muted text-ink',
 emerald: 'bg-emerald-50 text-emerald-700',
 rose: 'bg-rose-50 text-rose-700',
 gray: 'bg-gray-100 text-ink-secondary',
 };
 return (
 <div className="bg-surface rounded-lg border border-border p-4 shadow-sm">
 <div className="flex items-start justify-between gap-2">
 <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-tertiary">
 {label}
 </span>
 {Icon ? (
 <div className={`p-1.5 rounded-lg shrink-0 ${tones[tone]}`}>
 <Icon className="w-4 h-4" />
 </div>
 ) : null}
 </div>
 <p className="text-xl font-bold text-ink mt-2 tabular-nums">{value}</p>
 {hint ? <p className="text-[11px] text-ink-tertiary mt-1 leading-snug">{hint}</p> : null}
 </div>
 );
};

const limitLabel = (value, unit) =>
 value == null || value === '' ? `Unlimited ${unit}` : `${value} ${unit}`;

const AdminSubscriptions = () => {
 const [searchTerm, setSearchTerm] = useState('');
 const [statusFilter, setStatusFilter] = useState('all');
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [selectedSubscription, setSelectedSubscription] = useState(null);
 const [showEditModal, setShowEditModal] = useState(false);
 const { success, error: showError } = useToastContext();

 const queryParams = {
 is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
 sort_by: 'sort_order',
 sort_order: 'asc',
 };

 const {
 data: subscriptionsData,
 isLoading: loading,
 error: subscriptionsError,
 refetch,
 isFetching,
 } = useSubscriptions(queryParams);

 const {
 data: statsData,
 isLoading: statsLoading,
 refetch: refetchStats,
 } = useSubscriptionStats();

 const subscriptions = subscriptionsData?.data || [];
 const stats = statsData?.data || {};
 const totalSubscriptions = stats.total_subscriptions || 0;
 const activeSubscriptions = stats.active_subscriptions || 0;
 const inactiveSubscriptions = stats.inactive_subscriptions || 0;

 const tabCounts = {
 total: totalSubscriptions,
 active: activeSubscriptions,
 inactive: inactiveSubscriptions,
 };

 const createSubscriptionMutation = useCreateSubscription({
 onSuccess: () => {
 success('Plan created successfully');
 setShowCreateModal(false);
 refetch();
 refetchStats();
 },
 onError: (err) => {
 showError(err.message || 'Failed to create plan');
 },
 });

 const updateSubscriptionMutation = useUpdateSubscription({
 onSuccess: () => {
 success('Plan updated successfully');
 setShowEditModal(false);
 setSelectedSubscription(null);
 refetch();
 refetchStats();
 },
 onError: (err) => {
 showError(err.message || 'Failed to update plan');
 },
 });

 const deleteSubscriptionMutation = useDeleteSubscription({
 onSuccess: () => {
 success('Plan deleted');
 refetch();
 refetchStats();
 },
 onError: (err) => {
 showError(err.message || 'Failed to delete plan');
 },
 });

 const toggleStatusMutation = useToggleSubscriptionStatus({
 onSuccess: () => {
 success('Plan status updated');
 refetch();
 refetchStats();
 },
 onError: (err) => {
 showError(
 err.response?.data?.message || err.message || 'Failed to update plan status'
 );
 },
 });

 const filteredSubscriptions = useMemo(() => {
 const q = searchTerm.trim().toLowerCase();
 if (!q) return subscriptions;
 return subscriptions.filter(
 (s) =>
 s.name?.toLowerCase().includes(q) ||
 s.description?.toLowerCase().includes(q) ||
 s.stripe_price_id_monthly?.toLowerCase().includes(q) ||
 s.stripe_price_id_yearly?.toLowerCase().includes(q) ||
 s.stripe_product_id?.toLowerCase().includes(q)
 );
 }, [subscriptions, searchTerm]);

 const openEditModal = (subscription) => {
 setSelectedSubscription(subscription);
 setShowEditModal(true);
 };

 const handleDeleteSubscription = (subscription) => {
 const name = subscription?.name || 'this plan';
 if (!window.confirm(`Delete “${name}”? Vendors on this plan may lose access.`)) return;
 deleteSubscriptionMutation.mutate(subscription.id);
 };

 const columns = [
 {
 key: 'subscription',
 label: 'Plan',
 render: (subscription) => (
 <div className="min-w-0 max-w-xs">
 <div className="flex items-center gap-2">
 <p className="text-sm font-semibold text-ink truncate">
 {subscription.name}
 </p>
 <span className="text-[10px] font-medium tabular-nums text-gray-400">
 #{subscription.sort_order ?? 0}
 </span>
 </div>
 {subscription.description ? (
 <p className="text-xs text-ink-tertiary mt-0.5 line-clamp-2">
 {subscription.description}
 </p>
 ) : null}
 </div>
 ),
 },
 {
 key: 'price',
 label: 'Pricing',
 render: (subscription) => {
 const currency = subscription.currency || 'USD';
 const pm = subscription.price_monthly ?? subscription.price;
 const py = subscription.price_yearly;
 const hasDual =
 subscription.price_monthly != null || subscription.price_yearly != null;
 return (
 <div className="text-sm tabular-nums">
 {hasDual ? (
 <>
 <p className="font-medium text-ink">
 {money(pm, currency)}
 <span className="text-xs font-normal text-gray-500"> /mo</span>
 </p>
 <p className="text-xs text-ink-tertiary mt-0.5">
 {py != null ? `${money(py, currency)} /yr` : '— /yr'}
 </p>
 </>
 ) : (
 <p className="font-medium text-ink">
 {money(subscription.price, currency)}
 <span className="text-xs font-normal text-gray-500">
 {' '}
 /{subscription.billing_interval === 'yearly' ? 'yr' : 'mo'}
 </span>
 </p>
 )}
 </div>
 );
 },
 },
 {
 key: 'features',
 label: 'Limits',
 render: (subscription) => (
 <div className="text-xs text-ink-secondary space-y-0.5">
 <p>{limitLabel(subscription.max_bookings_per_month, 'bookings/mo')}</p>
 <p>{limitLabel(subscription.max_services, 'services')}</p>
 <p>{limitLabel(subscription.max_images, 'images')}</p>
 <p>
 {subscription.feature_ai_generator
 ? `AI ${limitLabel(subscription.max_ai_generations_per_month, 'writes/mo')}`
 : 'AI writer off'}
 </p>
 <p>{limitLabel(subscription.max_emails_per_month, 'emails/mo')}</p>
 </div>
 ),
 },
 {
 key: 'stripe_mapping',
 label: 'Stripe',
 render: (subscription) => {
 const m = subscription.stripe_price_id_monthly || subscription.stripe_price_id;
 const y = subscription.stripe_price_id_yearly;
 const mapped = Boolean(m || y);
 return (
 <div className="text-sm">
 <span
 className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${ mapped ? 'bg-emerald-50 text-emerald-800 border-emerald-200/80' : 'bg-amber-50 text-amber-900 border-amber-200/80' }`}
 >
 {mapped ? <Link2 className="w-3 h-3" /> : <Unlink className="w-3 h-3" />}
 {mapped ? 'Mapped' : 'Not mapped'}
 </span>
 {mapped ? (
 <div
 className="mt-1 text-[11px] text-ink-tertiary max-w-[200px] truncate"
 title={[m, y].filter(Boolean).join(' · ')}
 >
 {m ? <span className="block truncate">M: {m}</span> : null}
 {y ? <span className="block truncate">Y: {y}</span> : null}
 </div>
 ) : (
 <p className="mt-1 text-[11px] text-ink-tertiary">Add price IDs to bill</p>
 )}
 </div>
 );
 },
 },
 {
 key: 'is_active',
 label: 'Status',
 render: (subscription) =>
 subscription.is_active ? (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-800 border-emerald-200/80">
 <CheckCircle className="w-3 h-3" />
 Active
 </span>
 ) : (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
 <XCircle className="w-3 h-3" />
 Inactive
 </span>
 ),
 },
 ];

 const actions = [
 {
 label: 'Toggle',
 icon: Power,
 onClick: (subscription) => {
 toggleStatusMutation.mutate({
 id: subscription.id,
 isActive: !subscription.is_active,
 });
 },
 title: 'Toggle status',
 variant: 'default',
 },
 {
 label: 'Edit',
 icon: Edit,
 onClick: openEditModal,
 title: 'Edit plan',
 variant: 'default',
 },
 {
 label: 'Delete',
 icon: Trash2,
 onClick: handleDeleteSubscription,
 title: 'Delete plan',
 variant: 'default',
 },
 ];

 if (subscriptionsError) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Subscriptions"
 subtitle="Vendor SaaS plans sold by the platform"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-8 text-center shadow-sm">
 <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
 <h3 className="mt-3 text-lg font-medium text-ink">
 Couldn’t load plans
 </h3>
 <p className="mt-1 text-sm text-ink-tertiary">
 {subscriptionsError.message || 'Failed to load subscriptions'}
 </p>
 <Button className="rounded-md mt-5" onClick={() => refetch()}>
 Try again
 </Button>
 </div>
 </div>
 );
 }

 const createBusy =
 createSubscriptionMutation.isPending || createSubscriptionMutation.isLoading;
 const updateBusy =
 updateSubscriptionMutation.isPending || updateSubscriptionMutation.isLoading;

 return (
 <div className="space-y-5">
 <AdminPageHeader
 className="sm:items-center"
 title="Subscriptions"
 subtitle="Vendor SaaS plans — pricing, limits, and Stripe mapping"
 actions={
 <div className="flex flex-wrap items-center justify-end gap-2">
 <Link
 to="/admin/subscription-payments"
 className="inline-flex items-center justify-center gap-2 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <Receipt className="w-4 h-4" />
 Payments
 </Link>
 <Button className="rounded-md inline-flex items-center" onClick={() => setShowCreateModal(true)}>
 <Plus className="w-4 h-4 mr-1.5" />
 Add plan
 </Button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.subscriptions.title}
 steps={ADMIN_PAGE_GUIDES.subscriptions.steps}
 icon={ListChecks}
 />

 {loading || statsLoading ? (
 <div className="product-panel py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading plans…</p>
 </div>
 ) : (
 <>
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <MetricCard
 label="Total plans"
 value={totalSubscriptions}
 hint="All subscription products"
 icon={Crown}
 tone="sky"
 />
 <MetricCard
 label="Active"
 value={activeSubscriptions}
 hint="Visible for vendor checkout"
 icon={CheckCircle}
 tone="emerald"
 />
 <MetricCard
 label="Inactive"
 value={inactiveSubscriptions}
 hint="Hidden from new signups"
 icon={XCircle}
 tone="rose"
 />
 </div>

 <div className="product-panel overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Plan status"
 >
 {STATUS_TABS.map((tab) => {
 const isActive = statusFilter === tab.value;
 const count = tabCounts[tab.countKey];
 return (
 <button
 key={tab.value}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setStatusFilter(tab.value)}
 className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 {typeof count === 'number' ? (
 <span
 className={`text-[11px] tabular-nums px-1.5 py-0.5 rounded-md ${ isActive ? 'bg-gray-100 text-ink-secondary' : 'bg-gray-200/70 text-gray-500' }`}
 >
 {count}
 </span>
 ) : null}
 </button>
 );
 })}
 </div>

 <div className="relative w-full sm:w-64">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
 <input
 type="search"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 placeholder="Search plans or Stripe IDs…"
 className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-border bg-surface text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink"
 />
 </div>
 </div>

 <div className="p-4 sm:p-5 relative">
 {isFetching && !loading ? (
 <div className="absolute top-3 right-4 text-[11px] text-gray-400 flex items-center gap-1">
 <Loader2 className="w-3 h-3 animate-spin" />
 Updating
 </div>
 ) : null}
 <DataTable
 data={filteredSubscriptions}
 columns={columns}
 actions={actions}
 searchable={false}
 filterable={false}
 emptyMessage={
 searchTerm || statusFilter !== 'all'
 ? 'No plans match your search or filter.'
 : 'No subscription plans yet. Create one to get started.'
 }
 className="!border-0 !shadow-none"
 />
 </div>
 </div>
 </>
 )}

 <CreateSubscriptionModal
 isOpen={showCreateModal}
 onClose={() => setShowCreateModal(false)}
 onSave={(data) => createSubscriptionMutation.mutate(data)}
 isLoading={createBusy}
 />

 <CreateSubscriptionModal
 isOpen={showEditModal && !!selectedSubscription}
 onClose={() => {
 setShowEditModal(false);
 setSelectedSubscription(null);
 }}
 onSave={(data) =>
 updateSubscriptionMutation.mutate({
 id: selectedSubscription.id,
 ...data,
 })
 }
 subscription={selectedSubscription}
 isEdit
 isLoading={updateBusy}
 />
 </div>
 );
};

export default AdminSubscriptions;
